import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import { isIframePage } from '@shared/utils/helpers/is-iframe-page'

import { useAuthGuards } from '@pc/composables/auth-guards'
import { createConversation } from '@pc/composables/create-conversation'
import { CustomRoute } from '@pc/models/common/enums/custom-route'
import RouteNames from '@pc/router/route-names'
import ConversationPage from '@pc/views/ConversationPage.vue'
import HomePage from '@pc/views/HomePage.vue'

type Route = {
  name?: string
  query?: Record<string, string | number | null | string[]>
  params: Record<string, string | number | null | string[]>
}

export const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: RouteNames.home,
    component: HomePage,
    alias: '/chat',
    children: [
      {
        path: 'help',
        name: RouteNames.help,
        component: () => import('@pc/views/HelpScreen.vue'),
      },
      {
        path: 'conversation/:conversationId',
        name: RouteNames.conversation,
        component: ConversationPage,
        beforeEnter: async (to, from, next) => {
          const isIframe = isIframePage()

          /**
           * @description If the user is not on the iframe page and visitor, then we need to create a conversation
           * if user is on the iframe page, we create conversation on the App.vue so that the conversation
           * not show in reports before user start to interact with it.
           */
          if (
            !isIframe &&
            (!to.params?.conversationId ||
              to.params?.conversationId === CustomRoute.NULLABLE)
          ) {
            const data = (await createConversation(true)) as Route
            if (data?.params) {
              const params = {
                ...to,
                name: data.name ?? to.name,
                params: data.params ?? to.params,
              }
              next(params)
            }
          }
          next()
        },
        children: [
          {
            path: 'quick-form',
            name: RouteNames.quickFormPage,
            component: () => import('@pc/views/QuickFormPage.vue'),
            props: true,
          },
        ],
      },
      {
        path: 'guest-chat',
        name: RouteNames.guestChat,
        component: () => import('@pc/views/GuestChat.vue'),
      },
      {
        path: 'non-qliq-chat',
        name: RouteNames.nonQliqChat,
        component: () => import('@pc/views/NonQliqChat.vue'),
      },
      {
        path: '/view-tracker',
        component: () => import('@pc/views/ViewTracker/ViewTracker.vue'),
        name: RouteNames.viewTracker,
        redirect: (to) => ({
          name: RouteNames.viewTrackerIntro,
          query: { ...to.query },
        }),
        children: [
          {
            path: 'intro',
            name: RouteNames.viewTrackerIntro,
            component: () =>
              import('@pc/views/ViewTracker/ViewTrackerIntro.vue'),
          },
          {
            path: 'form',
            name: RouteNames.viewTrackerForm,
            component: () =>
              import('@pc/views/ViewTracker/ViewTrackerForm.vue'),
          },
          {
            path: 'results',
            name: RouteNames.viewTrackerResult,
            component: () =>
              import('@pc/views/ViewTracker/ViewTrackerResults.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/auth',
    name: RouteNames.authChallenge,
    component: () => import('@pc/views/AuthChallenge.vue'),
  },
  {
    path: '/tac',
    name: RouteNames.tac,
    component: () => import('@pc/views/TermsAndConditions.vue'),
  },
  {
    path: '/authentification-failed',
    name: RouteNames.authFailed,
    component: () => import('@pc/views/AuthentificationFailed.vue'),
  },
  {
    path: '/link-expired',
    name: RouteNames.linkExpired,
    component: () => import('@pc/views/LinkExpired.vue'),
  },
  {
    path: '/conversations',
    name: RouteNames.conversationsList,
    component: () => import('@pc/views/PatientConversations.vue'),
  },
  {
    path: '/landing',
    name: RouteNames.landingPage,
    component: () => import('@pc/views/LandingPage.vue'),
  },
  {
    path: '/widget-unavailable',
    name: RouteNames.widgetUnavailable,
    component: () => import('@pc/views/ChatWidgetUnavailable.vue'),
  },
]

export const base = process.env.VUE_APP_VC_ROUTER_BASE_PATH

const router = createRouter({
  history: createWebHistory(base),
  routes,
})

router.beforeEach(async (to) => {
  const { authBeforeEach } = useAuthGuards()

  return await authBeforeEach(to)
})

export default router
